import { CronDayType, CronIntervalType } from "./types";

export const CRON_DAY_OPTIONS = [
  {
    value: CronDayType.SUN,
    label: "Sun",
  },
  {
    value: CronDayType.MON,
    label: "Mon",
  },
  {
    value: CronDayType.TUE,
    label: "Tue",
  },
  {
    value: CronDayType.WED,
    label: "Wed",
  },
  {
    value: CronDayType.THU,
    label: "Thu",
  },
  {
    value: CronDayType.FRI,
    label: "Fri",
  },
  {
    value: CronDayType.SAT,
    label: "Sat",
  },
];

export const CRON_MONTH_OPTIONS = [
  {
    value: 1,
    label: "Jan",
  },
  {
    value: 2,
    label: "Feb",
  },
  {
    value: 3,
    label: "Mar",
  },
  {
    value: 4,
    label: "Apr",
  },
  {
    value: 5,
    label: "May",
  },
  {
    value: 6,
    label: "Jun",
  },
  {
    value: 7,
    label: "Jul",
  },
  {
    value: 8,
    label: "Aug",
  },
  {
    value: 9,
    label: "Sep",
  },
  {
    value: 10,
    label: "Oct",
  },
  {
    value: 11,
    label: "Nov",
  },
  {
    value: 12,
    label: "Dec",
  },
];

export const CRON_INTERVALS = [
  {
    dataKey: CronIntervalType.WEEKLY,
    label: "Weekly",
  },
  {
    dataKey: CronIntervalType.MONTHLY,
    label: "Monthly",
  },
];

export const getHourOptions = () =>
  Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 === 0 ? 12 : i % 12;
    const period = i < 12 ? "AM" : "PM";
    return {
      value: i.toString(),
      label: `${hour} ${period}`,
    };
  });

export const getMinutesOptions = () =>
  Array(60)
    .fill(0)
    .map((_, i) => ({
      value: i.toString(),
      label: i.toString(),
    }));

export const getDayOfMonthOptions = () =>
  Array(31)
    .fill(0)
    .map((_, i) => ({
      value: (i + 1).toString(),
      label: (i + 1).toString(),
    }));

/**
 * Maps AWS CloudWatch month aliases (`JAN`-`DEC`) to their numeric equivalents
 * (`1`-`12`), derived from {@link CRON_MONTH_OPTIONS} so the two stay in sync.
 * Keys are upper-cased to allow case-insensitive lookups.
 */
export const CRON_MONTH_ALIAS_TO_NUMBER: Record<string, number> =
  Object.fromEntries(
    CRON_MONTH_OPTIONS.map(({ label, value }) => [label.toUpperCase(), value])
  );

/**
 * Resolves a single cron token to a number, translating a known alias (e.g. a
 * month name like `JAN`) via `aliases` before falling back to numeric parsing.
 * Returns `NaN` when the token is neither a known alias nor an entirely numeric
 * string.
 *
 * Numeric parsing intentionally rejects tokens with trailing characters (e.g.
 * the AWS nearest-weekday token `15W` or `1W`) rather than using `parseInt`,
 * which would silently parse `15W` as `15` and coerce a nearest-weekday
 * schedule into a plain day-of-month.
 */
const resolveCronToken = (
  token: string,
  aliases: Record<string, number>
): number => {
  const aliasValue = aliases[token.toUpperCase()];
  if (aliasValue !== undefined) {
    return aliasValue;
  }
  return /^\d+$/.test(token) ? parseInt(token, 10) : NaN;
};

/**
 * Expands a single cron field expression into an ordered list of concrete numeric values.
 *
 * Supports comma-separated lists (`1,7`), ranges (`1-6`), step values
 * (`* /n`, `a-b/n`, `a/n`), and named aliases supplied via `aliases` (e.g.
 * AWS month names like `JAN,JUL`). Resolved values are constrained to the
 * inclusive `[min, max]` bounds of the field, and any token that cannot be
 * resolved to a number is ignored. This guards against `NaN` leaking into a
 * rebuilt cron expression when a stored value uses syntax the simplified input
 * cannot model directly (e.g. `*\/6` for "every 6 months").
 *
 * @param field - the raw cron field, e.g. "*\/6", "1-6", "1,7", or "JAN,JUL"
 * @param min - the smallest valid value for the field (e.g. 1 for months)
 * @param max - the largest valid value for the field (e.g. 12 for months)
 * @param aliases - optional map of upper-cased aliases to their numeric values
 *   (e.g. {@link CRON_MONTH_ALIAS_TO_NUMBER} for the month field)
 * @returns an ascending, de-duplicated list of numbers the field resolves to
 */
export const expandCronFieldToNumbers = (
  field: string,
  min: number,
  max: number,
  aliases: Record<string, number> = {}
): number[] => {
  const values = new Set<number>();

  field.split(",").forEach((part) => {
    const [rangePart, stepPart] = part.split("/");
    const step = stepPart ? parseInt(stepPart, 10) : 1;
    if (Number.isNaN(step) || step < 1) {
      return;
    }

    let rangeStart: number;
    let rangeEnd: number;
    if (rangePart === "*") {
      rangeStart = min;
      rangeEnd = max;
    } else if (rangePart.includes("-")) {
      const [start, end] = rangePart
        .split("-")
        .map((v) => resolveCronToken(v, aliases));
      rangeStart = start;
      rangeEnd = end;
    } else {
      rangeStart = resolveCronToken(rangePart, aliases);
      // a bare number paired with a step (e.g. "1/6") runs through to the max
      rangeEnd = stepPart ? max : rangeStart;
    }

    if (Number.isNaN(rangeStart) || Number.isNaN(rangeEnd)) {
      return;
    }

    for (let value = rangeStart; value <= rangeEnd; value += step) {
      if (value >= min && value <= max) {
        values.add(value);
      }
    }
  });

  return Array.from(values).sort((a, b) => a - b);
};
